<script>
  import {
    Select,
    Form,
    FormItem,
    Input,
    Option,
    OptionGroup,
    Switch,
    CheckboxGroup,
    Checkbox,
    RadioGroup,
    Radio,
    Button,
    InputNumber,
    DatePicker,
    Cascader,
    Upload,
    Autocomplete
  } from 'element-ui'

  import AsyncSelect from './async-select'
  import SelectTree from './select-tree'
  import dateRange from './date-range'

  import {
    isArray,
    isObject,
    isString,
    isNumber,
    isEmpty,
    getEmptyObject
  } from "../../utils";

  let components = {
    input: Input,
    select: Select,
    switch: Switch,
    checkbox: CheckboxGroup,
    radio: RadioGroup,
    cascader: Cascader,
    upload: Upload,
    'input-number': InputNumber,
    'date-picker': DatePicker,
    'async-select': AsyncSelect,
    'select-tree': SelectTree,
    'auto-complete': Autocomplete,
    'date-range': dateRange
  }

  const defaultValue = {
    input: '',
    select: function ({multiple}) {
      return multiple ? [] : ''
    },
    switch: false,
    checkbox: [],
    radio: '',
    'input-number': 0,
    'select-tree': '',
    'date-range': []
  }

  // 验证触发的时机
  const validateEventName = {
    input: 'blur',
    select: 'change',
    switch: 'change',
    checkbox: 'change',
    radio: 'change',
    cascader: 'change',
    'input-number': 'change',
    'async-select': 'change',
    'select-tree': 'change',
    'date-picker': 'blur',
    'auto-complete': 'blur',
    'date-range': 'input'
  }

  const formItemWidth = 200

  let validateStacks = []

  const registerComponents = function (config) {
    for(const [componentName, opt] of Object.entries(config)){
      components[componentName] = opt.component
      validateEventName[componentName] = opt.validateEvent || 'input'
      defaultValue[componentName] = opt.defaultValue || ''
    }
  }

  const getDefaultValue = function ({type, props = {}}) {
    const defaultVal = defaultValue[type]

    return typeof defaultVal === 'function' ? defaultVal(props) : defaultVal
  }

  /*
  * 获取表单控件默认props
  *
  * @item   {Object}
  * @return {Obejct}
  *
  */
  const getDefaultProps = function ({type, label = ''}) {
    let defaultProps

    switch (type){
      case 'input':
        defaultProps = {
          placeholder: `请输入${label}`
        }
        break
      case  'input-number':
        defaultProps = {
          min: 0
        }
        break
      case 'select':
        defaultProps = {
          placeholder: `请选择${label}`
        }
        break
      case 'cascader':
        defaultProps = {
          placeholder: `请选择${label}`
        }
        break
      case 'async-select':
        defaultProps = {
          placeholder: `请选择${label}`
        }
        break
      case 'select-tree':
        defaultProps = {
          selectProps: {
            placeholder: `请选择${label}`
          }
        }
        break
      case 'date-picker':
        defaultProps = {
          placeholder: '选择日期',
          'range-separator': '至',
          'start-placeholder': '开始日期',
          'end-placeholder': '结束日期',
        }
        break
    }

    return defaultProps
  }

  /*
  * 获取select控件的options
  *
  * @options {Array}
  * @return  {Array} VNode节点数组
  *
  */

  const getSelectOptionsVNodes = function (h, options, optionSlots, item) {
    const labelKey = item.labelKey || 'label'
    const valueKey = item.valueKey || 'value'

    return Array.from(options, option => h(
      Option,
      {
        props: {
          ...option,
          label: option[labelKey],
          value: item.isObjectValue ? option : (valueKey in option ? option[valueKey] : option[labelKey])
        }
      },
      [optionSlots ? optionSlots(option) : null])
    )
  }

  /*
  * 获取表单控件的子组件 例 select的option
  *
  * @item   {Object}
  * @return {Array} VNode节点数组
  *
  */

  const getChildrenVNode = function (item, vm) {
    const h = vm.$createElement

    const slots = vm.$slots[item.key]

    const scopedSlots = vm.$scopedSlots[item.key]

    switch(item.type) {
      case 'select':
        // 若用户没有传入value值 则将option 的value值 默认设置为label值
        return (
          item.group
            ?
            Array.from(item.options, group => h(
              OptionGroup,
              {
                props: {
                  label: group.label,
                  key: group.label
                }
              },
              getSelectOptionsVNodes(h, group.options, scopedSlots, item)
              )
            )
            :
            getSelectOptionsVNodes(h, item.options, scopedSlots, item)
        )
      case 'checkbox':
        return Array.from(item.checkbox, checkbox => h(
          Checkbox,
          {
            props: Object.assign({}, checkbox, {
              label: 'value' in checkbox ? checkbox.value : checkbox.label
            })
          },
          checkbox.label)
        )
      case 'radio':
        return Array.from(item.radio, radio => {
          return h(
            Radio,
            {
              props: Object.assign({}, radio, {
                label: 'value' in radio ? radio.value : radio.label
              })
            },
            radio.label)}
        )
      default:
        return slots && slots.map(slot => (slot.context = vm._self, slot))
    }
  }

  /*
  * 验证表单中某个字段值
  *
  * @validateOpt {Object} 当前控件的验证规则
  * @dataItem    {Object} 当前控件的数据
  * @return      {Boolan}
  *
  */

  const validate = function (vm, item) {
    const { validate: validateOpt } = item
    const formDataResult = vm.value
    const itemValue = formDataResult[item.key]
    let errorMsgs = vm.errorMsg
    let required
    let rules

    if(!validateOpt || item.visible === false) return true

    if(isObject(validateOpt)){
      required = validateOpt.required
      rules = validateOpt.rules || []
    }else if(isArray(validateOpt)){
      rules = validateOpt
    }

    errorMsgs[item.key] = ''

    if(required && isEmpty(itemValue)){
      errorMsgs[item.key] = validateOpt.msg || `请填写${item.label}`
      return false
    }else if(required === false && isEmpty(itemValue)){
      return true
    }

    for(const {type, value, msg} of rules){

      if(type === 'number' && (itemValue === '' || !isNumber(+itemValue))){
        errorMsgs[item.key] = msg
        break
      }

      if(type === 'minLength' && itemValue.length < value){
        errorMsgs[item.key] = msg
        break
      }

      if(type === 'maxLength' && itemValue.length > value){
        errorMsgs[item.key] = msg
        break
      }

      if(type === 'regexp' && !value.test(itemValue)){
        errorMsgs[item.key] = msg
        break
      }

      if(type === 'custom' && typeof value === 'function' && !value(formDataResult)){
        errorMsgs[item.key] = msg
        break
      }

    }

    return errorMsgs[item.key] === '' ? true : false
  }

  /*
  * 获取当前验证状态 className
  *
  * @errorMsg {String} 当前控件的错误提示  null->还没有进行过验证 ''->验证通过 否则验证不通过
  * @return   {null | String} 根据状态返回className
  *
  */

  const getValidateStatus = function (errorMsg) {
    switch (errorMsg){
      case null:
        return null
      case '':
        return 'is-success'
      default:
        return 'is-error'
    }
  }

  /*
  * 获取当前验证状态图标 className
  *
  * @errorMsg {String} 当前控件的错误提示  null->还没有进行过验证 ''->验证通过 否则验证不通过
  * @return   {null | String} 根据状态返回className
  *
  */

  const getValidateStatusIcon = function (errorMsg) {
    switch (errorMsg){
      case null:
        return null
      case '':
        return 'el-icon-circle-check'
      default:
        return 'el-icon-circle-close'
    }
  }

  const getScopedSlots = function (item, vm, handleInput) {
    let scopedSlots = {}

    for(const [slotStr, scopeSlot] of Object.entries(vm.$scopedSlots)){
      const [slotKey, slotName] = slotStr.split('__')

      if(slotKey === item.key) {
        if(slotName) {
          scopedSlots[slotName] = function (scope) {
            scope.input = handleInput

            return scopeSlot(scope)
          }
        }else {
          scopedSlots.default = scopeSlot
        }
      }
    }

    return Object.keys(scopedSlots).length ? scopedSlots : null
  }

  const getFormItemCollection = function (vm) {
    const h = vm.$createElement

    return Array.from(vm.formOptions, (item, idx) => {
      // 表单组件 例 input select
      if (item.type !== 'text' && item.type !== 'slot' && !components[item.type]) {
        console.error(`表单中${item.type}组件未注册`)
      }

      const formComponent = components[item.type]
      const formItemValue = item.key in vm.value ? vm.value[item.key] : getDefaultValue(item)
      const errorMsg = vm.errorMsg[item.key]
      const validateEvent = validateEventName[item.type]
      // 合并默认props 与 传入的props
      let properties = Object.assign({}, getDefaultProps(item), item.props)
      let props = {}
      let attrs = {}

      const handleInput = function (...arg) {
        const newVal = arg[0]

        vm.$emit('input', Object.assign({}, vm.value, {
          [item.key]: newVal
        }))

        item.on && item.on['input'] && item.on['input'](...arg)

        if(validateEventName[item.type] === 'input') {
          validateStacks.push(() => validate(vm, item))
        }
      }

      const scopedSlots = getScopedSlots(item, vm, handleInput)
      const formItenContentScopeSlot = scopedSlots
        && scopedSlots['form-item-content']
        && scopedSlots['form-item-content']({errorMsg, validate: () => validate(vm, item)})

      for(const [key, value] of Object.entries(properties)){
        if(formComponent && formComponent.props[key]){
          props[key] = value
        }else{
          attrs[key] = value
        }
      }

      return h(FormItem, {
          class: [
            item.key.replace(/([A-Z])/g,"-$1").toLowerCase(),
            item.statusIcon && getValidateStatus(errorMsg),
            item.statusIcon && 'el-form-item--feedback',
            item.rowspan && `el-form-item--row${item.rowspan}`,
            item.validate
            &&
            item.validate.required
            &&
            'is-required',
            {
              'hide': !(('visible' in item ? item.visible : true) && ('visibleContro' in item ? item.visibleContro : true))
            }
          ],
          props: {
            label: item.label,
            'label-width': vm.labelWidth
          }
        }, [...(formItenContentScopeSlot || [
          item.type !== 'text'
            ?
            h(formComponent, {
                ref: item.key,
                props: {
                  value: formItemValue,
                  // async-select
                  request: item.request,
                  getOptions: item.getOptions,
                  // validate
                  'suffix-icon': item.statusIcon && getValidateStatusIcon(errorMsg),
                  ...props
                },
                attrs,
                on: {
                  ...item.on,
                  [validateEvent]: (...arg) => {
                    const validateEvent = validateEventName[item.type]

                    item.on && item.on[validateEvent] && item.on[validateEvent](...arg)

                    if(validateEvent === 'change'){
                      validateStacks.push(() => validate(vm, item))
                    }else{
                      validate(vm, item)
                    }
                  },
                  input: handleInput
                },
                nativeOn: item.nativeOn,
                scopedSlots: item.type !== 'select' && scopedSlots
              },
              getChildrenVNode(item, vm))
            :
            h('div', formItemValue)
          ,
          // validate VNode
          item.validate && h('div', {
              attrs: {
                class: 'el-form-item__error'
              }
            },
            errorMsg
          )
        ]),
          h('template', {
            slot: 'label'
          }, scopedSlots && scopedSlots['label'] && scopedSlots['label']({label: item.label}))
        ]
      )
    })

  }

  const validateForm = function (vm) {
    return vm.formOptions.filter(item => !validate(vm, item)).length === 0
  }

  export default {
    name: 'FormGroup',
    registerComponents,
    props: {
      formOptions: Array,
      value: {
        type: Object,
        default: getEmptyObject
      },
      labelWidth: String,
      inline: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        errorMsg: {}
      }
    },
    methods: {
      reset () {
        let resetFormData = {}

        this.formOptions.forEach((item) => {
          const {
            key
          } = item

          resetFormData[key] = getDefaultValue(item)
          this.errorMsg[key] = null
        })

        this.$emit('input', resetFormData)
      },
      validate () {
        return validateForm(this)
      },
      getFormRefs () {
        return this.$refs
      }
    },
    watch: {
      value () {
        validateStacks.forEach(validateFn => validateFn())

        validateStacks.length = 0
      },
      formOptions: {
        immediate: true,
        handler (newOptions) {
          newOptions.forEach(({key}) => {
            if(!(key in this.errorMsg)) {
              this.$set(this.errorMsg, key, null)
            }
          })
        }
      }
    },
    render () {
      const vm = this
      const h = vm.$createElement
      // 表单控件项
      const formItemCollection = getFormItemCollection(vm)

      return h(Form, {
        class: ['pagoda-form-group', 'el-form-reset'],
        props: {
          inline: vm.inline
        }
      }, [...formItemCollection, vm.$slots.default])
    }
  }
</script>
